import React from 'react';

export default function LeaveApplyForm({
  state,
  actions,
}) {
  const isReadOnly = state.isView;

  return (
    <form onSubmit={actions.handleSaveAndExit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Main Form Fields Card (2 cols) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs overflow-hidden">
            {/* Card Header */}
            <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="material-symbols-outlined text-primary text-xl">
                  {state.isView ? 'visibility' : 'assignment'}
                </span>
                <h2 className="font-display text-base font-bold text-black">
                  {state.isView
                    ? 'View Leave Application'
                    : state.isEdit
                    ? 'Edit Leave Application'
                    : 'Leave Application'}
                </h2>
              </div>
              <span className="text-xs font-semibold text-slate-500 font-mono">
                {state.editId ? `ID: #${state.editId}` : 'New Application'}
              </span>
            </div>

            {/* Form Inputs */}
            <div className="p-6 space-y-5">
              {/* Apply Date (Read-only) */}
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <label className="text-xs font-bold text-slate-700 sm:text-right">
                  Apply Date
                </label>
                <div className="sm:col-span-3">
                  <input
                    type="text"
                    value={state.applyDate}
                    readOnly
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low/60 px-3.5 py-2.5 text-xs font-bold text-black cursor-not-allowed outline-hidden"
                  />
                </div>
              </div>

              {/* Employee (Read-only) */}
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <label className="text-xs font-bold text-slate-700 sm:text-right">
                  Employee
                </label>
                <div className="sm:col-span-3">
                  <input
                    type="text"
                    value={state.employee}
                    readOnly
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low/60 px-3.5 py-2.5 text-xs font-bold text-black cursor-not-allowed outline-hidden"
                  />
                </div>
              </div>

              {/* From Date */}
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <label className="text-xs font-bold text-slate-700 sm:text-right">
                  From Date {!isReadOnly && <span className="text-rose-500">*</span>}
                </label>
                <div className="sm:col-span-3">
                  <input
                    type="date"
                    value={state.fromDate}
                    onChange={(e) => actions.setFromDate(e.target.value)}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    required={!isReadOnly}
                    className={`w-full rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-semibold text-black outline-hidden transition ${
                      isReadOnly
                        ? 'bg-surface-container-low/60 cursor-not-allowed'
                        : 'bg-surface-container-lowest focus:border-primary focus:ring-1 focus:ring-primary'
                    }`}
                  />
                </div>
              </div>

              {/* Till Date */}
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <label className="text-xs font-bold text-slate-700 sm:text-right">
                  Till Date {!isReadOnly && <span className="text-rose-500">*</span>}
                </label>
                <div className="sm:col-span-3">
                  <input
                    type="date"
                    value={state.tillDate}
                    onChange={(e) => actions.setTillDate(e.target.value)}
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                    required={!isReadOnly}
                    className={`w-full rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-semibold text-black outline-hidden transition ${
                      isReadOnly
                        ? 'bg-surface-container-low/60 cursor-not-allowed'
                        : 'bg-surface-container-lowest focus:border-primary focus:ring-1 focus:ring-primary'
                    }`}
                  />
                </div>
              </div>

              {/* Type (FullDay / HalfDay) */}
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <label className="text-xs font-bold text-slate-700 sm:text-right">
                  Type {!isReadOnly && <span className="text-rose-500">*</span>}
                </label>
                <div className="sm:col-span-3 flex items-center gap-3">
                  <select
                    value={state.type}
                    onChange={(e) => actions.setType(e.target.value)}
                    disabled={isReadOnly}
                    className={`flex-1 rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-bold text-black outline-hidden transition ${
                      isReadOnly
                        ? 'bg-surface-container-low/60 cursor-not-allowed'
                        : 'bg-surface-container-lowest focus:border-primary cursor-pointer'
                    }`}
                  >
                    {state.typeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="rounded-xl border border-outline-variant/20 bg-surface-container-high px-3.5 py-2 text-xs font-extrabold text-black shrink-0 font-sans tabular-nums">
                    {state.days} {state.days === 1 ? 'Day' : 'Days'}
                  </div>
                </div>
              </div>

              {/* Leave Type */}
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <label className="text-xs font-bold text-slate-700 sm:text-right">
                  Leave Type {!isReadOnly && <span className="text-rose-500">*</span>}
                </label>
                <div className="sm:col-span-3">
                  <select
                    value={state.leaveType}
                    onChange={(e) => actions.setLeaveType(e.target.value)}
                    disabled={isReadOnly}
                    required={!isReadOnly}
                    className={`w-full rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-bold text-black outline-hidden transition ${
                      isReadOnly
                        ? 'bg-surface-container-low/60 cursor-not-allowed'
                        : 'bg-surface-container-lowest focus:border-primary cursor-pointer'
                    }`}
                  >
                    {state.leaveTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reason */}
              <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-2 sm:gap-4">
                <label className="text-xs font-bold text-slate-700 sm:text-right">
                  Reason {!isReadOnly && <span className="text-rose-500">*</span>}
                </label>
                <div className="sm:col-span-3">
                  <select
                    value={state.reason}
                    onChange={(e) => actions.setReason(e.target.value)}
                    disabled={isReadOnly}
                    required={!isReadOnly}
                    className={`w-full rounded-xl border border-outline-variant/30 px-3.5 py-2.5 text-xs font-bold text-black outline-hidden transition ${
                      isReadOnly
                        ? 'bg-surface-container-low/60 cursor-not-allowed'
                        : 'bg-surface-container-lowest focus:border-primary cursor-pointer'
                    }`}
                  >
                    {state.reasonOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Remarks */}
              <div className="grid grid-cols-1 sm:grid-cols-4 items-start gap-2 sm:gap-4">
                <label className="text-xs font-bold text-slate-700 sm:text-right pt-2.5">
                  Remarks {!isReadOnly && <span className="text-rose-500">*</span>}
                </label>
                <div className="sm:col-span-3">
                  <textarea
                    rows={4}
                    value={state.remarks}
                    onChange={(e) => actions.setRemarks(e.target.value)}
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    placeholder="Enter detailed reason and handover plan..."
                    required={!isReadOnly}
                    className={`w-full rounded-xl border border-outline-variant/30 p-3.5 text-xs text-black font-medium outline-hidden transition ${
                      isReadOnly
                        ? 'bg-surface-container-low/60 cursor-not-allowed'
                        : 'bg-surface-container-lowest focus:border-primary'
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Leave Balance Card (1 col) matching Screenshot 2 */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs overflow-hidden">
            <div className="flex items-center justify-between border-b border-outline-variant/20 bg-surface-container-low/40 px-6 py-4">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-base">
                  account_balance_wallet
                </span>
                <h3 className="font-display text-sm font-extrabold tracking-wide uppercase text-black">
                  Leave Balance
                </h3>
              </div>
              <span className="material-symbols-outlined text-sm text-slate-400">
                search
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low/20 text-[10px] font-extrabold uppercase tracking-wider text-slate-600">
                    <th className="py-3 px-4">Leave Type</th>
                    <th className="py-3 px-4 text-right">Current Balance</th>
                    <th className="py-3 px-4 text-right">Consume</th>
                    <th className="py-3 px-4 text-right">Laps</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/15">
                  {state.leaveBalances.map((bal, idx) => (
                    <tr key={idx} className="hover:bg-surface-container-low/40 transition-colors">
                      <td className="py-3 px-4 font-bold text-black whitespace-nowrap">
                        {bal.leaveType}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-black font-sans tabular-nums whitespace-nowrap">
                        {bal.currentBalance}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-slate-700 font-sans tabular-nums whitespace-nowrap">
                        {bal.consume}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-slate-500 font-sans tabular-nums whitespace-nowrap">
                        {bal.laps}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border-t border-outline-variant/15 bg-surface-container-low/10 px-6 py-3">
              <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                * Leave balances update automatically once pending requests are approved by management.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Sticky Actions Toolbar */}
      <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 bg-surface-container-lowest p-4 rounded-2xl shadow-xs">
        {isReadOnly ? (
          /* View Mode: Only Cancel button matching screenshot */
          <button
            type="button"
            onClick={actions.handleCancel}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-5 py-2 text-xs font-bold text-slate-800 hover:bg-surface-container-high transition cursor-pointer shadow-xs"
          >
            Cancel
          </button>
        ) : (
          /* Edit / New Mode */
          <>
            <button
              type="button"
              onClick={actions.handleCancel}
              className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-slate-700 hover:bg-surface-container-high transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={actions.handleSaveAndContinue}
              disabled={state.isSaving}
              className="rounded-xl border border-outline-variant/40 bg-surface-container-high px-4 py-2 text-xs font-bold text-black hover:bg-surface-container-highest transition cursor-pointer"
            >
              {state.isSaving ? 'Saving...' : 'Save & Continue'}
            </button>
            <button
              type="submit"
              disabled={state.isSaving}
              className="rounded-xl bg-primary px-5 py-2 text-xs font-bold text-white shadow-xs hover:bg-primary/90 transition cursor-pointer"
            >
              {state.isSaving ? 'Saving...' : 'Save & Exit'}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
